import { FC, useEffect, useRef, useState } from "react";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { FormType } from "../../common";
import { useFormContext } from "react-hook-form";
import { BeefInputGrowingIntervention, BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { BeefBaseline, BeefGrowingStageData } from "../../../../models/Baseline/BeefBaseline";
import BeefInputIntSourceComponent from "./BeefInputIntSourceComponent";
import BeefInputExtSourceComponent from "./BeefInputExtSourceComponent";
import { StageType } from "../../../../../../graphql/types";
import BeefInputProductivityComponent from "./BeefInputProductivityComponent";
import BeefInputBatchGrowingRepetitionComponent from "./BeefInputBatchGrowingRepetitionComponent";
import BeefInputStockComponent from "./BeefInputStockComponent";
import { Spacer } from "../../../helpers/Spacer";
import BeefInputGrowingNewAnimalsComponent from "./BeefInputGrowingNewAnimalsComponent";
import { cloneDeep } from "lodash";

interface BeefInputGrowingDialogProps {
  formType: FormType,
  stageIndex: number,
  baseline: BeefBaseline,
  productionProcessName: string,
  handleCancel: (param?: string | null) => void;
}

const BeefInputGrowingDialog: FC<BeefInputGrowingDialogProps> = ({
  stageIndex,
  formType,
  baseline,
  productionProcessName,
  handleCancel,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fieldItemPrefix = `stages.${stageIndex}.stageData.input`;

  const fc = useFormContext<BeefIntervention>();
  const currResetValue = useRef<BeefInputGrowingIntervention>();

  useEffect(() => {
    currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
  }, []);

  const [isProductivityEdit, setIsProductivityEdit] = useState<boolean>(false);
  const [isStockEdit, setIsStockEdit] = useState<boolean>(false);
  const [isRepetitionEdit, setIsRepetitionEdit] = useState<boolean>(false);
  const [isNewAnimalsEdit, setIsNewAnimalsEdit] = useState<boolean>(false);
  const [isIntEdit, setIsIntEdit] = useState<boolean>(false);
  const [isExtEdit, setIsExtEdit] = useState<boolean>(false);

  const isInputEdit =
    isProductivityEdit ||
    isStockEdit ||
    isRepetitionEdit ||
    isNewAnimalsEdit ||
    isIntEdit ||
    isExtEdit;

  const currentStageData = baseline.stages[stageIndex].stageData;
  const beefInputData = (currentStageData as BeefGrowingStageData).input;

  const isBatchGrowingOperation = beefInputData.growingOperation === "batchGrowing";

  const productivityCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.cattleProductivity === undefined)
      fc.setValue(`${fieldItemPrefix}.cattleProductivity`, "");

    if (isInnerCancel)
      fc.setValue(`${fieldItemPrefix}.cattleProductivity`, currResetValue.current?.cattleProductivity);

    setIsProductivityEdit(false);
  };

  const stockCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.animalsPresentAtStart === undefined) {
      fc.setValue(`${fieldItemPrefix}.animalsPresentAtStart`, "");
      fc.setValue(`${fieldItemPrefix}.animalsPresentAtEnd`, "");
      fc.setValue(`${fieldItemPrefix}.averageWeightAtStart`, "");
      fc.setValue(`${fieldItemPrefix}.averageWeightAtEnd`, "");
    }

    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.animalsPresentAtStart`, currResetValue.current?.animalsPresentAtStart);
      fc.setValue(`${fieldItemPrefix}.animalsPresentAtEnd`, currResetValue.current?.animalsPresentAtEnd);
      fc.setValue(`${fieldItemPrefix}.averageWeightAtStart`, currResetValue.current?.averageWeightAtStart);
      fc.setValue(`${fieldItemPrefix}.averageWeightAtEnd`, currResetValue.current?.averageWeightAtEnd);
    }

    setIsStockEdit(false);
  };

  const repetitionCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.numberOfRepetitions === undefined)
      fc.setValue(`${fieldItemPrefix}.numberOfRepetitions`, "");

    if (isInnerCancel)
      fc.setValue(`${fieldItemPrefix}.numberOfRepetitions`, currResetValue.current?.numberOfRepetitions);

    setIsRepetitionEdit(false);
  }

  const newAnimalsCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.averageAgeAtStart === undefined) {
      fc.setValue(`${fieldItemPrefix}.averageAgeAtStart`, "");
      fc.setValue(`${fieldItemPrefix}.averageWeightNewAnimals`, "");
    }

    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.averageAgeAtStart`, currResetValue.current?.averageAgeAtStart);
      fc.setValue(`${fieldItemPrefix}.averageWeightNewAnimals`, currResetValue.current?.averageWeightNewAnimals);
    }

    setIsNewAnimalsEdit(false);
  };

  const intSourceCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.internalSources?.at(0)?.numberAnimals === undefined) {
      (beefInputData?.internalSources || []).forEach((intSource, index) => {
        const fieldPrefix = `${fieldItemPrefix}.internalSources[${index}]`;
        Object.keys(intSource).forEach((key) => {
          fc.setValue(`${fieldPrefix}.${key}`, "")
        })
      })
    }

    if (isInnerCancel)
      fc.setValue(`${fieldItemPrefix}.internalSources`, currResetValue.current?.internalSources);

    setIsIntEdit(false);
  };

  const extSourceCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.externalSources?.at(0)?.numberAnimals === undefined) {
      (beefInputData?.externalSources || []).forEach((intSource, index) => {
        const fieldPrefix = `${fieldItemPrefix}.externalSources[${index}]`;
        Object.keys(intSource).forEach((key) => {
          fc.setValue(`${fieldPrefix}.${key}`, "")
        })
      })
    }

    if (isInnerCancel)
      fc.setValue(`${fieldItemPrefix}.externalSources`, currResetValue.current?.externalSources);

    setIsExtEdit(false);
  };

  const handleDialogCancelClick = () => {
    productivityCancelHandler(false);
    stockCancelHandler(false);
    repetitionCancelHandler(false);
    newAnimalsCancelHandler(false);
    intSourceCancelHandler(false);
    extSourceCancelHandler(false);

    fc.setValue(`${fieldItemPrefix}`, currResetValue.current);

    if (handleCancel) {
      handleCancel();
    }
  };

  return (
    <DialogContainer
      formVisible
      handleClose={handleDialogCancelClick}
      iconCode="general/check-heart"
      formTitle={intl.formatMessage({ id: "SUSTELL.PROCESS.DIALOG.STAGE.ANIMALS.TITLE" })}
      datasetType="intervention"
      width={isInputEdit ? "1068px" : "800px"}
    >
      <BeefInputProductivityComponent
        formType={formType}
        stageType={StageType.Growing}
        fieldItemPrefix={fieldItemPrefix}
        defaultStageData={beefInputData}
        isBatchGrowingOperation={isBatchGrowingOperation}
        isProductivityEdit={isProductivityEdit}
        setIsProductivityEdit={setIsProductivityEdit}
        productivityCancelHandler={productivityCancelHandler}
      />

      {isBatchGrowingOperation ? (
        <>
          <Spacer height="16px" />
          <BeefInputBatchGrowingRepetitionComponent
            formType={formType}
            fieldItemPrefix={fieldItemPrefix}
            defaultStageData={beefInputData}
            isRepetitionEdit={isRepetitionEdit}
            setIsRepetitionEdit={setIsRepetitionEdit}
            repetitionCancelHandler={repetitionCancelHandler}
          />
          <Spacer />
        </>
      ) : (
        <BeefInputStockComponent
          formType={formType}
          stageType={StageType.Growing}
          fieldItemPrefix={fieldItemPrefix}
          defaultStageData={beefInputData}
          isStockEdit={isStockEdit}
          setIsStockEdit={setIsStockEdit}
          stockCancelHandler={stockCancelHandler}
        />
      )}

      <p className={classes.headingTitleVariant}>
        {intl.formatMessage({ id: "SUSTELL.STAGE.CATTLE.INPUT.NEW_ANIMALS" })}
      </p>
      <p>
        {intl.formatMessage({ id: "SUSTELL.STAGE.CATTLE.INPUT.NEW_ANIMALS_DESCRIPTION" })}
      </p>

      <BeefInputGrowingNewAnimalsComponent
        formType={formType}
        fieldItemPrefix={fieldItemPrefix}
        defaultStageData={beefInputData}
        isNewAnimalsEdit={isNewAnimalsEdit}
        setIsNewAnimalsEdit={setIsNewAnimalsEdit}
        newAnimalsCancelHandler={newAnimalsCancelHandler}
      />

      <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
        <div>
          <label
            htmlFor="internalSources"
            className={classes.headingTitleVariant}
          >
            {intl.formatMessage({
              id: "SUSTELL.STAGE.BEEF.INPUT.INTERNAL_SOURCE",
            })}
          </label>
          <div>
            <BeefInputIntSourceComponent
              formType={formType}
              stageType={StageType.Growing}
              fieldItemPrefix={fieldItemPrefix}
              productionProcessName={productionProcessName}
              defaultStageData={beefInputData}
              isInternalEdit={isIntEdit}
              setIsInternalEdit={setIsIntEdit}
              intSourceCancelHandler={intSourceCancelHandler}
              baseline={baseline}
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="externalSources"
            className={classes.headingTitleVariant}
          >
            {intl.formatMessage({
              id: "SUSTELL.STAGE.BEEF.INPUT.EXTERNAL_SOURCE",
            })}
          </label>
          <div>
            <BeefInputExtSourceComponent
              formType={formType}
              stageType={StageType.Growing}
              fieldItemPrefix={fieldItemPrefix}
              defaultStageData={beefInputData}
              isExternalEdit={isExtEdit}
              setIsExternalEdit={setIsExtEdit}
              extSourceCancelHandler={extSourceCancelHandler}
            />
          </div>
        </div>
      </DsmGrid>

      <DsmButtonControlGroup
        cancelHandler={handleDialogCancelClick}
        saveHandler={handleCancel}
        saveLabel={intl.formatMessage({ id: "GENERAL.CONFIRM" })}
      />
    </DialogContainer>
  )
}

export default BeefInputGrowingDialog;
